/* eslint-disable import/no-extraneous-dependencies */
/* globals AHEHC, am5geodataWorldLow */

import {
	Root,
	Tooltip,
	Picture,
	Bullet,
	color as makeColor,
	percent,
} from '@amcharts/amcharts5';
import {
	GraticuleSeries,
	MapChart,
	MapPolygonSeries,
	MapPointSeries,
	geoOrthographic,
	getGeoRectangle,
} from '@amcharts/amcharts5/map';

const COLORS = {
	yellow: makeColor( 0xFFDB57 ),
	green: makeColor( 0x8DC965 ),
	purple: makeColor( 0x977BB6 ),
	grey: makeColor( 0xE3DFD7 ),
};

export default function setupGlobe( element ) {
	// Create root element
	// https://www.amcharts.com/docs/v5/getting-started/#Root_element
	const root = Root.new( element );

	// Create the map chart
	// https://www.amcharts.com/docs/v5/charts/map-chart/
	const chart = root.container.children.push(
		MapChart.new( root, {
			width: percent( 84 ),
			height: percent( 84 ),
			x: percent( 7 ),
			y: percent( 7 ),
			projection: geoOrthographic(),
			rotationX: -15, // try to center africa
			rotationY: -15, // tilt down a bit
			panX: 'rotateX',
			panY: 'rotateY',
			wheelX: 'none',
			wheelY: 'none',
			minZoomLeveL: 1,
			maxZoomLevel: 1,
		} ),
	);

	// Create series for background fill
	// https://www.amcharts.com/docs/v5/charts/map-chart/map-polygon-series/#Background_polygon
	const backgroundSeries = chart.series.push( MapPolygonSeries.new( root, {} ) );
	backgroundSeries.mapPolygons.template.setAll( {
		fill: makeColor( 0x025752 ),
		fillOpacity: 0.2,
		strokeOpacity: 0,
	} );

	// Add background polygon
	// https://www.amcharts.com/docs/v5/charts/map-chart/map-polygon-series/#Background_polygon
	backgroundSeries.data.push( {
		geometry: getGeoRectangle( 90, 180, -90, -180 ),
	} );

	// Create graticule series
	// https://www.amcharts.com/docs/v5/charts/map-chart/graticule-series/
	const graticuleSeries = chart.series.push( GraticuleSeries.new( root, {} ) );
	graticuleSeries.mapLines.template.setAll( {
		stroke: makeColor( 0x025752 ),
		strokeOpacity: 0.3,
	} );

	// Create main polygon series for countries
	// https://www.amcharts.com/docs/v5/charts/map-chart/map-polygon-series/
	chart.series.push(
		MapPolygonSeries.new( root, {
			fill: makeColor( 0x025752 ),
			stroke: makeColor( 0x025752 ),
			geoJSON: am5geodataWorldLow,
		} ),
	);

	// Create point series for markers
	// https://www.amcharts.com/docs/v5/charts/map-chart/map-point-series/
	const pointSeries = chart.series.push( MapPointSeries.new( root, {
		tooltip: Tooltip.new( root, {
			pointerOrientation: 'up',
		} ),
	} ) );

	pointSeries.bullets.push( function() {
		const sprite = Picture.new( root, {
			width: 26,
			height: 38,
			centerX: percent( 50 ),
			centerY: percent( 100 ),
			stroke: makeColor( 0x000000 ),
			tooltipText: '{title}',
			templateField: 'bulletSettings',
		} );

		sprite.events.on( 'pointerover', event => {
			const item = document.getElementById( event.target.dataItem.dataContext.id );
			if ( item ) {
				item.classList.add( 'highlighted' );
				item.scrollIntoView( { block: 'nearest', behavior: 'smooth' } );
			}
		} );

		sprite.events.on( 'pointerout', event => {
			const item = document.getElementById( event.target.dataItem.dataContext.id );
			if ( item ) {
				item.classList.remove( 'highlighted' );
				item.scrollIntoView( { block: 'nearest', behavior: 'smooth' } );
			}
		} );

		sprite.events.on( 'click', event => {
			const item = document.getElementById( event.target.dataItem.dataContext.id );
			if ( item ) {
				item.querySelector( 'button[aria-controls], a[href]' )?.click();
			}
		} );

		return Bullet.new( root, {
			sprite,
		} );
	} );

	function addMarker( { lng, lat, id, title, color } ) {
		pointSeries.data.push( {
			id,
			geometry: { type: 'Point', coordinates: [ lng, lat ] },
			title,
			bulletSettings: {
				src: AHEHC.assetsUrl + `/img/marker-${ color }.svg`,
				fill: COLORS[ color ],
			},
		} );
	}

	const markers = JSON.parse( element.dataset.markers || '[]' );
	for ( const marker of markers ) {
		addMarker( marker );
	}

	// Make stuff animate on load
	chart.appear( 1000, 100 );

	return { chart, pointSeries };
}
